import { supabase } from '../../../shared/lib/supabase';

// Define the ApiKey interface based on your sample data
export interface ApiKey {
  id: string;
  key_name: string;
  api_key: string;
  organisation_id: string;
  access_type: string;
  rate_limit_per_hour: number;
  is_active: boolean;
  created_at: string;
  expires_at: string | null;
  // Joined fields
  organisation_name?: string;
}

export interface ApiKeyCreateInput {
  key_name: string;
  api_key: string;
  organisation_id: string;
  access_type?: string;
  rate_limit_per_hour: number;
  is_active: boolean;
  expires_at?: string | null;
}

export interface ApiKeyUpdateInput {
  key_name?: string;
  api_key?: string;
  access_type?: string;
  rate_limit_per_hour?: number;
  is_active?: boolean;
  expires_at?: string | null;
}

class ApiKeysService {
  async getAll(): Promise<ApiKey[]> {
    try {
      console.log('🔍 Fetching API keys with joins...');

      const { data, error } = await supabase
        .from('api_keys')
        .select(`
          *,
          organisations!api_keys_organisation_id_fkey(name)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching API keys:', error);
        throw error;
      }

      // Transform the data to include joined fields
      const transformedData = data?.map(item => ({
        ...item,
        organisation_name: item.organisations?.name || ''
      })) || [];

      console.log('✅ API keys fetched successfully:', transformedData.length, 'records');
      return transformedData;
    } catch (error) {
      console.error('❌ API keys fetch error:', error);
      throw error;
    }
  }

  async getById(id: string): Promise<ApiKey | null> {
    try {
      console.log(`🔍 Fetching API key with ID: ${id}`);

      const { data, error } = await supabase
        .from('api_keys')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('❌ Error fetching API key by ID:', error);
        throw error;
      }

      console.log('✅ API key fetched successfully:', data);
      return data;
    } catch (error) {
      console.error('❌ API key fetch by ID error:', error);
      throw error;
    }
  }

  async create(input: ApiKeyCreateInput): Promise<ApiKey> {
    try {
      console.log('📝 Creating API key:', input);

      // Filter out undefined values to avoid database issues
      const cleanInput = Object.fromEntries(
        Object.entries(input).filter(([_, value]) => value !== undefined)
      );

      const { data: result, error } = await supabase
        .from('api_keys')
        .insert(cleanInput)
        .select()
        .single();

      if (error) {
        console.error('❌ Error creating API key:', error);
        throw error;
      }

      console.log('✅ API key created successfully:', result);
      return result;
    } catch (error) {
      console.error('❌ API key creation error:', error);
      throw error;
    }
  }

  async update(id: string, data: ApiKeyUpdateInput): Promise<ApiKey> {
    try {
      console.log(`📝 Updating API key ${id}:`, data);

      const { data: result, error } = await supabase
        .from('api_keys')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('❌ Error updating API key:', error);
        throw error;
      }

      console.log('✅ API key updated successfully:', result);
      return result;
    } catch (error) {
      console.error('❌ API key update error:', error);
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      console.log(`🗑️ Deleting API key: ${id}`);

      const { error } = await supabase
        .from('api_keys')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('❌ Error deleting API key:', error);
        throw error;
      }

      console.log('✅ API key deleted successfully');
    } catch (error) {
      console.error('❌ API key deletion error:', error);
      throw error;
    }
  }
}

export const apiKeysService = new ApiKeysService();
